import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, Trash2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { PageHeader, EmptyState, StatusBadge } from "@/components/Common";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { PdfPreviewDialog } from "@/components/PdfPreviewDialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function DeliveryOrders() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["delivery-orders"], queryFn: api.listDeliveryOrders });
  const [confirmId, setConfirmId] = useState(null);
  const [preview, setPreview] = useState(null);

  const deleteMut = useMutation({
    mutationFn: (id) => api.deleteDeliveryOrder(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["delivery-orders"] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setConfirmId(null);
      toast.success("Deleted");
    },
  });

  return (
    <div data-testid="delivery-orders-page">
      <PageHeader overline={t("delivery_orders")} title={t("delivery_orders")} />

      {isLoading ? (
        <p className="text-muted-foreground">{t("loading")}</p>
      ) : data.length === 0 ? (
        <EmptyState
          title={t("no_data")}
          hint={t("convert_to_do")}
          action={
            <Link to="/orders">
              <Button data-testid="goto-orders-btn" className="bg-primary hover:bg-primary/90">
                {t("orders")}
              </Button>
            </Link>
          }
        />
      ) : (
        <div className="border border-border rounded-md bg-white overflow-x-auto" data-testid="dos-table-wrap">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-border">
                <TableHead className="overline">{t("do_number")}</TableHead>
                <TableHead className="overline">{t("order_ref")}</TableHead>
                <TableHead className="overline">{t("client")}</TableHead>
                <TableHead className="overline">{t("delivery_date")}</TableHead>
                <TableHead className="overline">{t("status")}</TableHead>
                <TableHead className="overline text-right">{t("units")}</TableHead>
                <TableHead className="overline text-right">{t("actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((d) => (
                <TableRow key={d.id} data-testid={`do-row-${d.id}`}>
                  <TableCell className="font-medium mono">
                    <Link to={`/delivery-orders/${d.id}`} className="hover:underline text-primary">
                      {d.do_number}
                    </Link>
                  </TableCell>
                  <TableCell className="mono text-xs text-muted-foreground">{d.order_number || "—"}</TableCell>
                  <TableCell>{d.client_snapshot?.name || "—"}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">{fmtDate(d.delivery_date)}</TableCell>
                  <TableCell><StatusBadge value={d.status} /></TableCell>
                  <TableCell className="text-right mono font-medium" data-testid={`do-units-${d.id}`}>
                    {(d.items || []).reduce((s, l) => s + Number(l.qty || 0), 0)}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Link to={`/delivery-orders/${d.id}`}>
                      <Button data-testid={`view-do-${d.id}`} variant="ghost" size="sm">
                        <Eye className="size-3.5" />
                      </Button>
                    </Link>
                    <Button data-testid={`pdf-do-${d.id}`} variant="ghost" size="sm" className="text-primary" onClick={() => setPreview({ id: d.id, number: d.do_number })}>
                      <FileDown className="size-3.5" />
                    </Button>
                    <Button
                      data-testid={`delete-do-${d.id}`}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => setConfirmId(d.id)}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirm_delete")}</AlertDialogTitle>
            <AlertDialogDescription>
              Deleting a delivery order restores its stock and reverts the linked client order.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-do-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-do-ok"
              onClick={() => deleteMut.mutate(confirmId)}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <PdfPreviewDialog
        open={!!preview}
        onOpenChange={(v) => !v && setPreview(null)}
        title={preview ? `Delivery Order ${preview.number || ""}` : ""}
        filename={preview ? `delivery-order-${preview.number || preview.id}.pdf` : "delivery-order.pdf"}
        fetchBlob={preview ? () => api.fetchDeliveryOrderPdfBlob(preview.id) : null}
        fetchPngBlob={preview ? () => api.fetchDeliveryOrderPngBlob(preview.id) : null}
        pngFilename={preview ? `delivery-order-${preview.number || preview.id}.png` : null}
      />

    </div>
  );
}
